// coordinateSystem.ts

/** Basic numeric range */
export interface Extents {
  min: number;
  max: number;
}

/** Document/canvas logical size (in client space units) */
export interface Size {
  width: number;
  height: number;
}

/** 2D canvas transform matrix (ctx.setTransform signature) */
export interface Matrix {
  a: number; // scaleX
  b: number; // skewY
  c: number; // skewX
  d: number; // scaleY
  e: number; // translateX
  f: number; // translateY
}

/** View (zoom/pan) */
export interface View {
  scale: number;
  x: number;
  y: number;
}

/** A point in view space, plus the corresponding client-space position */
export interface ViewPoint {
  x: number;
  y: number;
  relativeClientX: number;
  relativeClientY: number;
}

/** A point in client space (plus some aliases) */
export interface ClientPoint {
  clientX: number;
  clientY: number;
  x?: number; // alias of clientX
  y?: number; // alias of clientY
  relativeX?: number;
  relativeY?: number;
}

/** Bounds of the canvas in both client and view space */
export interface CanvasBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
  canvasWidth: number;
  canvasHeight: number;
  viewMin: ViewPoint;
  viewMax: ViewPoint;
}

/** Canvas-like object we care about */
export interface CanvasLike {
  width: number;
  height: number;
  getBoundingClientRect: () => Pick<DOMRect, "left" | "top" | "right" | "bottom">;
}

/** Listener for view changes */
export type ViewListener = (view: View) => void;

/** Identity transform */
export const IDENTITY: Matrix = Object.freeze({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

const NULL_VIEW_POINT: ViewPoint = Object.freeze({
  x: 0,
  y: 0,
  relativeClientX: 0,
  relativeClientY: 0,
});

const NULL_BOUNDS = Object.freeze({
  canvasWidth: 0,
  canvasHeight: 0,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  viewMin: NULL_VIEW_POINT,
  viewMax: NULL_VIEW_POINT,
}) as CanvasBounds;

/**
 * Facilitates calculation and manipulation of a zoom- and pan-able view within a canvas.
 */
export default class CoordinateSystem {
  private _scaleExtents: Extents;
  private _documentSize: Size;

  private _canvas: CanvasLike | null = null;
  private _view: View = { scale: 1.0, x: 0, y: 0 };

  private _viewChangeListeners: Set<ViewListener> = new Set();

  constructor({ scaleExtents, documentSize }: { scaleExtents?: Extents; documentSize: Size }) {
    this._scaleExtents = scaleExtents ?? { min: 0.33, max: 3 };
    this._documentSize = documentSize;
  }

  /** Canvas getter/setter */
  get canvas(): CanvasLike | null {
    return this._canvas;
  }
  set canvas(canvas: CanvasLike | null) {
    this._canvas = canvas;
    this.setView(); // recalc with new canvas
  }

  /** Zoom factor */
  get scale(): number {
    return this._view.scale;
  }
  setScale = (scale: number) => {
    this.setView({ scale });
  };

  /** Pan X */
  get x(): number {
    return this._view.x;
  }
  set x(x: number) {
    this.setView({ x });
  }

  /** Pan Y */
  get y(): number {
    return this._view.y;
  }
  set y(y: number) {
    this.setView({ y });
  }

  /** Copy of current view */
  get view(): View {
    return { ...this._view };
  }

  /** Scale extents */
  get scaleExtents(): Extents {
    return { ...this._scaleExtents };
  }
  set scaleExtents({ min, max }: Extents) {
    this._scaleExtents = { min, max };
    this.setView();
  }

  /** Document size (constrains pan) */
  get documentSize(): Size {
    return { ...this._documentSize };
  }
  set documentSize({ width, height }: Size) {
    this._documentSize = { width, height };
    this.setView();
  }

  /** Current transform matrix */
  get transformMatrix(): Matrix {
    return {
      a: this._view.scale,
      b: 0,
      c: 0,
      d: this._view.scale,
      e: this._view.x,
      f: this._view.y,
    };
  }

  /** Canvas bounds in client & view space */
  get canvasBounds(): CanvasBounds | undefined {
    if (!this._canvas) return undefined;
    const { left, top, right, bottom } = this._canvas.getBoundingClientRect();
    return {
      viewMin: this.clientPointToViewPoint({ clientX: left, clientY: top }),
      viewMax: this.clientPointToViewPoint({ clientX: right, clientY: bottom }),
      left,
      top,
      right,
      bottom,
      canvasWidth: this._canvas.width,
      canvasHeight: this._canvas.height,
    };
  }

  /** Raw client rect subset */
  private get canvasRect(): Pick<DOMRect, "left" | "top" | "right" | "bottom"> | undefined {
    return this._canvas?.getBoundingClientRect();
  }

  /** Clamp a proposed view to extents & document bounds */
  clampView = ({ scale, x, y }: Partial<View> & View): View => {
    const { min, max } = this.scaleExtents;
    const { width, height } = this.documentSize;
    const { left, top, right, bottom } = this.canvasRect || NULL_BOUNDS;

    const canvasWidth = right - left;
    const canvasHeight = bottom - top;

    const maxx = canvasWidth / 2;
    const minx = -(width * this._view.scale - canvasWidth / 2);
    const maxy = canvasHeight / 2;
    const miny = -(height * this._view.scale - canvasHeight / 2);

    return {
      scale: Math.min(Math.max(scale, min), max),
      x: Math.min(Math.max(x, minx), maxx),
      y: Math.min(Math.max(y, miny), maxy),
    };
  };

  /** Reset to identity view */
  resetView = () => {
    this.setView({ scale: 1.0, x: 0, y: 0 });
  };

  /** Apply a (partial) view update with clamping. Returns the applied view. */
  setView(view?: Partial<View>): View {
    const newView = this.clampView({ ...this._view, ...(view || {}) } as View);
    const { scale, x, y } = this._view;

    if (newView.scale !== scale || newView.x !== x || newView.y !== y) {
      this._view = newView;
      this._viewChangeListeners.forEach((listener) => listener && listener(newView));
    }
    return { ...this._view };
  }

  /**
   * Zoom by deltaScale around a specific client point (keeps that point stable).
   */
  scaleAtClientPoint = (deltaScale: number, clientPoint: ClientPoint): View => {
    const viewPt = this.clientPointToViewPoint(clientPoint);
    const newView = this.clampView({ ...this._view, scale: this._view.scale + deltaScale });

    const clientPtPostScale = this.viewPointToClientPoint(viewPt, newView);
    newView.x = this._view.x - (clientPtPostScale.clientX - clientPoint.clientX);
    newView.y = this._view.y - (clientPtPostScale.clientY - clientPoint.clientY);

    return this.setView(newView);
  };

  /** Convert a client-space point to view-space */
  clientPointToViewPoint = (
    { clientX, clientY }: ClientPoint,
    view: View = this._view
  ): ViewPoint => {
    const { left, top } = this.canvasRect || NULL_BOUNDS;
    const relativeClientX = clientX - left;
    const relativeClientY = clientY - top;

    return {
      x: (relativeClientX - view.x) / view.scale,
      y: (relativeClientY - view.y) / view.scale,
      relativeClientX,
      relativeClientY,
    };
  };

  /** Convert a view-space point to client-space */
  viewPointToClientPoint = (
    { x, y }: { x: number; y: number },
    view: View = this._view
  ): Required<ClientPoint> => {
    const { left, top } = this.canvasRect || NULL_BOUNDS;
    const relativeX = x * view.scale + view.x;
    const relativeY = y * view.scale + view.y;
    const clientX = relativeX + left;
    const clientY = relativeY + top;

    return { clientX, clientY, relativeX, relativeY, x: clientX, y: clientY };
  };

  /** Subscribe to view changes */
  attachViewChangeListener = (listener: ViewListener) => {
    this._viewChangeListeners.add(listener);
  };
}
